import React from 'react'
import { DataTableColumn, Badge, Button, Icon, Flex } from '@popety-io/ui'
import type { BuildingPermit } from '@popety-io/types'
import type { DefaultTheme } from 'styled-components'
import type { TFunction } from 'i18next'

// Helper functions for rendering
const getArchitect = (permit: BuildingPermit): string => {
  return permit.architect || '—'
}

const getOwner = (permit: BuildingPermit): string => {
  return permit.owner || '—'
}

const getTotalLandArea = (permit: BuildingPermit): number | null => {
  if (!permit.lands || permit.lands.length === 0) return null
  return permit.lands.reduce((total, land) => total + (land.area || 0), 0)
}

const formatLandArea = (area: number | null): string => {
  if (!area) return '—'
  return `${area.toLocaleString('fr-CH')} m²`
}

const getPrimaryOfficialId = (permit: BuildingPermit): string => {
  return permit.permit_number || permit.id.toString()
}

const renderDate = (date: Date | string | undefined): string => {
  if (!date) return '—'

  const dateObj = typeof date === 'string' ? new Date(date) : date
  return dateObj.toLocaleDateString('fr-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

const renderSize = (permit: BuildingPermit) => {
  const totalArea = getTotalLandArea(permit)
  return formatLandArea(totalArea)
}

const renderDescription = (permit: BuildingPermit) => {
  if (!permit.description) return '—'

  const truncated =
    permit.description.length > 100
      ? `${permit.description.substring(0, 100)}...`
      : permit.description

  return <span title={permit.description}>{truncated}</span>
}

// Status configuration mapping API values to translation keys
const getStatusConfig = (
  status: string,
  t: TFunction
): { variant: 'success' | 'warning' | 'danger' | 'default'; label: string } => {
  const statusMap: Record<
    string,
    { variant: 'success' | 'warning' | 'danger' | 'default'; key: string }
  > = {
    Réalisé: { variant: 'success', key: 'realized' },
    Accordé: { variant: 'success', key: 'approved' },
    'En instruction': { variant: 'warning', key: 'underReview' },
    Refusé: { variant: 'danger', key: 'refused' },
    'En attente': { variant: 'warning', key: 'pending' },
    Enregistrement: { variant: 'default', key: 'registered' },
    // English statuses
    approved: { variant: 'success', key: 'approved' },
    pending: { variant: 'warning', key: 'pending' },
    instruction: { variant: 'warning', key: 'underReview' },
    rejected: { variant: 'danger', key: 'rejected' },
    completed: { variant: 'success', key: 'completed' },
    enregistrement: { variant: 'default', key: 'registered' },
  }

  const config = statusMap[status]
  if (config) {
    return {
      variant: config.variant,
      label: t(`buildingPermits.statuses.${config.key}`),
    }
  }

  return { variant: 'default', label: status }
}

// Category configuration mapping API values to translation keys
const getCategoryLabel = (category: string, t: TFunction): string => {
  const categoryKeys: Record<string, string> = {
    development: 'development',
    renovation: 'renovation',
    transformation: 'transformation',
    elevation: 'elevation',
    regularisation: 'regularisation',
  }

  const key = categoryKeys[category]
  if (key) {
    return t(`buildingPermits.categories.${key}`)
  }

  return category
}

// Define the shared columns configuration
export const getBuildingPermitsColumns = (
  columnWidths: Record<string, number> = {},
  onToggleSave?: (permit: BuildingPermit) => void,
  savedPermitIds: string[] = [],
  context: 'main' | 'analysis' | 'estimate' = 'analysis',
  theme?: DefaultTheme,
  t?: TFunction
): DataTableColumn<BuildingPermit>[] => {
  // Fallback for missing t function
  const translate = t || ((key: string) => key.split('.').pop() || key)

  // Custom cell renderers that need translation
  const renderStatus = (permit: BuildingPermit) => {
    const status = permit.result || permit.status
    if (!status) return '—'

    const config = getStatusConfig(status, translate)
    return <Badge variant={config.variant}>{config.label}</Badge>
  }

  const renderCategory = (permit: BuildingPermit) => {
    if (!permit.category) return '—'
    return getCategoryLabel(permit.category, translate)
  }

  // Base columns array
  const baseColumns: DataTableColumn<BuildingPermit>[] = [
    {
      key: 'permit_number',
      label: translate('buildingPermits.columns.permitNumber'),
      sortable: true,
      width: `${columnWidths['permit_number'] || 150}px`,
      render: (permit) => getPrimaryOfficialId(permit),
      exportFormatter: (permit) => getPrimaryOfficialId(permit),
    },
    {
      key: 'filing_date',
      label: translate('buildingPermits.columns.filingDate'),
      sortable: true,
      width: `${columnWidths['filing_date'] || 120}px`,
      render: (permit) => renderDate(permit.filing_date),
      exportFormatter: (permit) => renderDate(permit.filing_date),
    },
    {
      key: 'status',
      label: translate('buildingPermits.columns.status'),
      sortable: true,
      width: `${columnWidths['status'] || 120}px`,
      render: renderStatus,
      exportFormatter: (permit) => permit.result || permit.status || '',
    },
    {
      key: 'category',
      label: translate('buildingPermits.columns.category'),
      sortable: true,
      width: `${columnWidths['category'] || 130}px`,
      render: renderCategory,
      exportFormatter: (permit) => permit.category || '',
    },
    {
      key: 'type',
      label: translate('buildingPermits.columns.type'),
      sortable: true,
      width: `${columnWidths['type'] || 150}px`,
      render: (permit) => permit.type || '—',
      exportFormatter: (permit) => permit.type || '',
    },
    {
      key: 'address',
      label: translate('buildingPermits.columns.address'),
      sortable: true,
      width: `${columnWidths['address'] || 250}px`,
      render: (permit) => permit.address || '—',
      exportFormatter: (permit) => permit.address || '',
    },
    {
      key: 'zone',
      label: translate('buildingPermits.columns.zone'),
      sortable: true,
      width: `${columnWidths['zone'] || 100}px`,
      render: (permit) => permit.zone || '—',
      exportFormatter: (permit) => permit.zone || '',
    },
    {
      key: 'owner',
      label: translate('buildingPermits.columns.owner'),
      sortable: true,
      width: `${columnWidths['owner'] || 200}px`,
      render: (permit) => getOwner(permit),
      exportFormatter: (permit) => getOwner(permit),
    },
    {
      key: 'architect',
      label: translate('buildingPermits.columns.architect'),
      sortable: true,
      width: `${columnWidths['architect'] || 200}px`,
      render: (permit) => getArchitect(permit),
      exportFormatter: (permit) => getArchitect(permit),
    },
    {
      key: 'size',
      label: translate('buildingPermits.columns.size'),
      sortable: true,
      width: `${columnWidths['size'] || 100}px`,
      render: renderSize,
      exportFormatter: (permit) => {
        const area = getTotalLandArea(permit)
        return area ? area.toString() : ''
      },
    },
    {
      key: 'description',
      label: translate('buildingPermits.columns.description'),
      sortable: false,
      width: `${columnWidths['description'] || 250}px`,
      render: renderDescription,
      exportFormatter: (permit) => permit.description || '',
    },
    {
      key: 'distance',
      label: translate('buildingPermits.columns.distance'),
      sortable: true,
      width: `${columnWidths['distance'] || 100}px`,
      render: (permit) => (permit.distance ? `${permit.distance}m` : '—'),
      exportFormatter: (permit) =>
        permit.distance ? permit.distance.toString() : '',
    },
    {
      key: 'approval_date',
      label: translate('buildingPermits.columns.approvalDate'),
      sortable: true,
      width: `${columnWidths['approval_date'] || 120}px`,
      render: (permit) => renderDate(permit.approval_date),
      exportFormatter: (permit) => renderDate(permit.approval_date),
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      width: '80px',
      render: (permit: BuildingPermit) => {
        // Only render the button if onToggleSave and theme are provided
        if (!onToggleSave || !theme) {
          console.warn('BuildingPermits: onToggleSave or theme missing', {
            hasToggleSave: !!onToggleSave,
            hasTheme: !!theme,
          })
          return null
        }

        const isSaved = savedPermitIds.includes(permit.id)

        return (
          <Flex gap="4px">
            <Button
              variant="ghost"
              size="small"
              onClick={(e) => {
                console.log('BuildingPermits: Save button clicked', {
                  permitId: permit.id,
                  isSaved,
                })
                e.stopPropagation()
                onToggleSave(permit)
              }}
              title={
                isSaved
                  ? translate('buildingPermits.actions.removeFromSaved')
                  : translate('buildingPermits.actions.savePermit')
              }
            >
              <Icon
                name="bookmark"
                size="small"
                color={
                  isSaved ? theme.colors.primary : theme.colors.text.secondary
                }
                fill={isSaved ? theme.colors.primary : 'transparent'}
                strokeWidth={2}
              />
            </Button>
          </Flex>
        )
      },
    },
  ]

  return baseColumns
}

// Default visible columns for different contexts
export const DEFAULT_VISIBLE_COLUMNS = {
  // For main building permits page - show more columns
  main: [
    'permit_number',
    'filing_date',
    'status',
    'category',
    'type',
    'address',
    'zone',
    'owner',
    'architect',
    'size',
    'description',
    'actions',
  ],
  // For analysis pages - show fewer columns
  analysis: [
    'permit_number',
    'filing_date',
    'status',
    'category',
    'address',
    'distance',
    'owner',
    'description',
    'actions',
  ],
  // For estimate pages - show minimal columns
  estimate: [
    'permit_number',
    'filing_date',
    'status',
    'category',
    'address',
    'distance',
    'description',
    'actions',
  ],
}
